import { useCallback, useEffect, useState } from 'react'
const { /* Platform, */ useWindowDimensions, KeyboardAvoidingView } = XPReactNative
import { 
  KeyBoardAvoidView, XStack, YStack, XPPlatform as Platform, XPReactNative, Image, ActionButton, ActiveButton, SimpleText, Spinner,
  useMedia
} from '@my/ui'
import { useUser } from '@clerk/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { color, MiscUtils, RandomId, TZAApiClient, TZABrandGuidelines, useAuthedRequestFn } from '@my/util'
import assets from '../../assets/images'
import {
  ChatInput,
  ChatMessages,
  FinancialReviewHeading,
  HomeMainHeading,
  SuggestedQuestions,
} from '../../components/index'
import { flexRender } from '@tanstack/react-table'
import images from '../../assets/images'
import { SquarePen } from '@tamagui/lucide-icons-2'
import { FinancialProfileButton } from '../home/helper'
import { TZATermsTextLinks } from '../termsfeed'
import { TypingIndicator } from './TypingIndicator'
export { TypingIndicator } from './TypingIndicator'

export const NoticeSection: React.FC<{acknowledge: () => void }> = ({ acknowledge }) => (
  <YStack style={{
    backgroundColor: color.dullGrey,
    width: '100%',
    padding: 10,
    //marginTop: 10,
    //borderRadius: 10
  }}>
    <XStack mb={'$2'} style={{position: 'relative'}}>
      <SimpleText fontWeight={'700'} fontSize={18} color={color.grey}>
        Disclaimer:
      </SimpleText>
      <ActionButton
        unstyled
        onPress={() => acknowledge()}
        style={{ position: 'absolute', top: 0, right: 0, borderRadius: 99 }}
        hoverStyle={{backgroundColor: 'rgb(250,250,250)'}}
      >
        <Image src={images.cross} style={{height: 20, width: 20}} />
      </ActionButton>
    </XStack>
    <SimpleText color={color.grey}>
      This is an AI-powered advisor. Responses may contain errors or inaccuracies. Always verify important financial decisions.
    </SimpleText>
  </YStack>
)

const genDefaultMessages = () => ([{
  message: `Hey Zillennial Fam! ✨ I’m Savvy—your personal financial advisor. Whether you're figuring out if you can afford a night out, checking in on your goals, or just curious where your money's been going, I've got your back. What can I help with today?`,
  itsMe: false,
  time: new Date().valueOf(),
  avatar: assets?.TZALogo,
  isInitialMessage: true,
  conversationId: RandomId(),
  msgId: RandomId(),
}]);

const SavvyChatDS = {
  messages: [] as any[]
};

export const useSavvyChat = () => {
  const { user } = useUser()
  const queryClient = useQueryClient()
  const authedRequestFn = useAuthedRequestFn()
  const [messages, setStateMessages] = useState<any[]>(SavvyChatDS.messages.length ? SavvyChatDS.messages : genDefaultMessages());
  const setMessages = (newMessages: any[]) => {
    SavvyChatDS.messages = newMessages;
    setStateMessages(newMessages);
  }
  const clearMessages = () => setMessages(genDefaultMessages());
  const [systemIsTyping, setSystemIsTyping] = useState<boolean>(false);
  const reviewDataQuery = useQuery({
    queryKey: ['financial-review'],
    queryFn: () => authedRequestFn('/financial-review'),
  })
  const suggestedQuestions: string[] = reviewDataQuery?.data?.suggestedQuestions || [];

  const [showNotice, setShowNotice] = useState(false);
  const userDataQuery = useQuery({
    queryKey: ['user-profile'],
    queryFn: () => authedRequestFn('/user-profile'),
  })

  const mutation = useMutation({
    mutationFn: (data: any) =>
      authedRequestFn('/user-profile', { method: 'POST', body: data }),
    onMutate: () => {
      //LongPollingSvc.trigger('financial-review');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['user-profile'] });
    },
    onSettled: () => {
      //setShowNotice(false);
      //@ts-ignore
    },
  })

  useEffect(() => {
    if (!userDataQuery.isLoading && !userDataQuery.data?.['Acknowledged Savvy Disclaimer']) {
      setShowNotice(true);
    }
  }, [userDataQuery.isLoading, userDataQuery.data]);
  
  const acknowledgeNotice = () => {
    const data = userDataQuery?.data;
    data['Acknowledged Savvy Disclaimer'] = true;
    setShowNotice(false);
    mutation.mutate(data);
  }

  const onChatInput = useCallback(
    async (message: string) => {
      if (message) {
        const newMessage = {
          message,
          itsMe: true,
          time: new Date().valueOf(),
          avatar: user?.imageUrl || undefined,
          msgId: RandomId(),
          conversationId: messages[0].conversationId,
        };
        let updatedMessages = [newMessage, ...messages];
        setMessages(updatedMessages);
        setSystemIsTyping(true);
        const chatRes = await TZAApiClient.chat(
          [...updatedMessages].reverse().slice(1),//.map((msg) => msg.message),
          { authedRequestFn }
        );
        setSystemIsTyping(false);
        updatedMessages = [
          {
            ...chatRes,
            avatar: assets?.TZALogo,
          },
          ...updatedMessages,
        ];
        setMessages(updatedMessages);
        /* setMessages((prev) => [
          ...(prev || []),
          {
            message: chatRes.systemResponse,
            itsMe: false,
            time: new Date().valueOf(),
            avatar: assets?.TZALogo,
          },
        ]) */
      }
    },
    [messages, user?.imageUrl, authedRequestFn]
  )

  const notice = {
    show: showNotice,
    acknowledge: acknowledgeNotice
  };

  const editMessage = (msg: any, editObj: any) => {
    const newMessageArray = [...messages];
    const msgIdx = newMessageArray.findIndex((item) => (item === msg));
    if (msgIdx > -1) {
      Object.assign(newMessageArray[msgIdx], editObj);
      setMessages(newMessageArray);
    }
  }
  const actionFns = {
    helpfulnessFn: (item: any, rating: "helpful" | "not-helpful") => editMessage(item, { rating }),
    reportFn: (item: any, issueDetails: string) => editMessage(item, { issueReported: true }),
  }
  
  return [messages, onChatInput, suggestedQuestions, systemIsTyping, clearMessages, notice, actionFns] as const;
}

export const Savvy = () => {
  const media = useMedia();
  const authedRequestFn = useAuthedRequestFn()
  const [messages, onChatInput, suggestedQuestions, systemIsTyping, clearMessages, notice, actionFns ] = useSavvyChat();
  const { control, reset, getValues } = useForm({
    defaultValues: {
      chatInput: '',
    },
  })

  const financialProfileQuery = useQuery({
    queryKey: ['financial-profile'],
    queryFn: () => authedRequestFn('/financial-profile'),
  });
  const { data, isLoading } = useQuery({
    queryKey: ['financial-review'],
    queryFn: () => authedRequestFn('/financial-review'),
  })
  const financialProfileCompleted = (Object.keys(financialProfileQuery?.data || {}).length > 1) && data?.financialSnapshotUI;
  const dataIsLoading = financialProfileQuery.isLoading || isLoading;

  const handleSubmit = (text: string) => {
    if (text.trim()) {
      onChatInput(text)
      reset()
    }
  }

  const GoToFinancialProfileButton = (
    <YStack alignItems={'center'}>
      <FinancialProfileButton />
    </YStack>
  );
  const mainChatUI = <>
    {/* Chat Body */}

    {/* <YStack style={{ flex: 1, width: '100%', alignItems: 'center' }} borderWidth={1}> */}
      <YStack style={{
        marginBottom: media['max-sm'] ? 0 : 10
      }} flex={1} width={'100%'} paddingHorizontal={20}>
        {/* Chat Messages */}
        <ChatMessages messages={messages} actionFns={actionFns} />
        {/* Suggested Questions and Input */}
        <YStack $platform-web={{ width: '100%', marginTop: 1 }} style={{ gap: 10, paddingBottom: 20, width: '100%' }} className='savvyChatList'> 
          {messages.length === 1 && (
            <SuggestedQuestions
              suggestedQuestions={suggestedQuestions}
              handleSubmit={handleSubmit}
            />
          )}
          {systemIsTyping ? <TypingIndicator /> : null}
          <ChatInput control={control} onSubmit={handleSubmit} getValues={getValues} />
        </YStack>

      </YStack>
    {/* </YStack> */}
  </>;

  const displayContent = dataIsLoading
  ? <Spinner size="large" color={TZABrandGuidelines.colorsMap.primaryGreen} /> 
  : financialProfileCompleted
    ? mainChatUI
    : GoToFinancialProfileButton

  const almostThere = <>
    {/* Header */}
    <YStack
      gap={10}
      flexWrap="wrap"
      flexDirection="row"
      width={'100%'}
      {...(messages.length > 1 ? {justifyContent: "space-between"} : {})}
      alignItems={'center'}
    >
      <YStack>
        <FinancialReviewHeading
          title="Savvy"
          img={assets.savvy}
          imageStyle={{
            //'$platform-native': { width: 26, height: 22 },
            //'$platform-web': { width: 24, height: 26 },
            resizeMode: 'contain',
          }}
        />
      </YStack>
      {messages.length > 2 ? <ActiveButton
        icon={<SquarePen />}
        style={{width: 140}}
        onPress={async () => {
          const shouldClear = await MiscUtils.dialog.confirm("Are you sure?", "(Proceeding will delete Savvy's responses and start a new chat.)");
          if (shouldClear) {
            clearMessages()
          }
        }}
        ButtonTextProps={{children: 'New Chat'}}
      /> : null}
    </YStack>
    {notice.show ? <XStack style={{
      borderRadius: 10,
      width: '100%',
      overflow: 'hidden'
    }}><NoticeSection acknowledge={notice.acknowledge} /></XStack> : null}
    {displayContent}
  </>

  return (
    <YStack 
      alignItems={'center'} 
      bg={'#fff'} 
      width={'100%'} 
      flex={1}
      padding={media['max-sm'] ? 8 : "$4"} 
      paddingHorizontal={media['max-sm'] ? 2 : undefined} 
      overflow="hidden" 
      gap={0}
    >
      <KeyboardAvoidingView 
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={{ flex: 1, maxWidth: '100vw', gap:10 }}
        keyboardVerticalOffset={90} // Adjust based on your header height
      >
        {almostThere}
      </KeyboardAvoidingView>
      
    </YStack>
  )
}
